/**
 * Camp Import/Export Service
 * Handles CSV import/export operations for camp data
 */

const API_BASE = '/api/admin';

/**
 * Export camps to CSV
 * @param {Array} fields - Optional array of field keys to export
 * @param {Object} filters - Optional filters (search, category, source)
 * @returns {Promise<Blob>} CSV file blob
 */
export const exportCampsToCSV = async (fields = null, filters = {}) => {
  try {
    const response = await fetch(`${API_BASE}/camps/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({ fields, filters })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to export camps');
    }

    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Error exporting camps:', error);
    throw error;
  }
};

/**
 * Download CSV file
 * @param {Blob} blob - CSV blob
 * @param {string} filename - Optional filename
 */
export const downloadCSV = (blob, filename = null) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `camps-export-${Date.now()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Import camps from CSV file
 * @param {File} file - CSV file
 * @returns {Promise<Object>} Import results with successful, failed, and errors
 */
export const importCampsFromCSV = async (file) => {
  try {
    // Read file as text
    const csvText = await readFileAsText(file);

    // Send to backend for processing
    const response = await fetch(`${API_BASE}/camps/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({ csv: csvText })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to import camps');
    }

    const result = await response.json();
    return result.results;
  } catch (error) {
    console.error('Error importing camps:', error);
    throw error;
  }
};

/**
 * Read file as text
 * @param {File} file - File to read
 * @returns {Promise<string>} File contents as text
 */
const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * Get auth token from Supabase session
 * @returns {Promise<string>} Auth token
 */
const getAuthToken = async () => {
  try {
    // Import supabase client dynamically to avoid circular dependencies
    const { supabase } = await import('@/lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || '';
  } catch (error) {
    console.error('Error getting auth token:', error);
    return '';
  }
};

/**
 * Validate CSV file before import
 * @param {File} file - CSV file to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateCSVFile = (file) => {
  const errors = [];

  if (!file) {
    errors.push('No file selected');
    return { isValid: false, errors };
  }

  if (!file.name.endsWith('.csv')) {
    errors.push('File must be a CSV file');
  }

  if (file.size === 0) {
    errors.push('File is empty');
  }

  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    errors.push('File size exceeds 10MB limit');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

